import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, Trash2, Lock } from 'lucide-react';
import { entriesApi } from '../../../../api/entries';
import { Project, TimeEntryStatus } from '../../../../types';
import { STATUS_CONFIG, FROZEN_STATUS_CONFIG, CLOSED_STATUS_CONFIG } from '../../../../components/ui/organisms/calendar-view';
import { quickHoursToRangeFrom, routesToDirectManager, MIN_DAILY_HOURS, isHalfHourStep, HALF_HOUR_ERROR } from '../../../../lib/timeEntryHelpers';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { DialogHeader, DialogTitle } from '../../../../components/ui/organisms/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { SearchableSelect } from '../../../../components/ui/molecules/searchable-select';
import {
  containsInternalMeeting, INTERNAL_MEETING_ERROR,
  hhmm, isDayComplete, layOutDay, getEntryProjectInactiveStatus,
} from '../utils';

interface NewRow {
  key: number;
  projectId: string;
  phaseId: string;
  hours: number;
  notes: string;
}

/**
 * Shown whenever a change would leave the day at something other than 8h,
 * whether that's an edit in flight (`pending`) or an entry being deleted
 * (`initialRemoved`). The employee closes the gap here — adjusting any entry an
 * approver hasn't picked up yet, or adding new ones — and nothing is written,
 * the deletion included, until the day totals exactly 8h.
 */
export function FinalizeDayDialog({
  date, pending, initialRemoved, projects, defaultJobTitleId, onBack, onSaved,
}: {
  date: string;
  /** The edit awaiting save, when the dialog was opened from the edit form. */
  pending?: { id: string; body: any } | null;
  /** Entries already marked for removal, when opened from a delete. */
  initialRemoved?: string[];
  projects: Project[];
  defaultJobTitleId: string;
  onBack: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Hour overrides keyed by entry id; the edit in flight seeds its own.
  const [hours, setHours] = useState<Record<string, number>>(
    pending ? { [pending.id]: pending.body.hours } : {},
  );
  const [newRows, setNewRows] = useState<NewRow[]>([]);
  // Ids dropped from the day; nothing is deleted until the day is saved.
  const [removed, setRemoved] = useState<string[]>(initialRemoved ?? []);

  const { data: dayEntries } = useQuery({
    queryKey: ['my-entries-for-day', date],
    // Rejected entries aren't worked time: the backend leaves them out of the
    // day's total and its overlap check, so they must not count here either.
    queryFn: () =>
      entriesApi.listMine({ from: date, to: date, limit: 50 })
        .then((r) => (r.data?.data ?? []).filter((e) => e.status !== 'REJECTED')),
  });

  const rows = useMemo(
    () =>
      (dayEntries ?? []).filter((e: any) => !removed.includes(e.id)).map((e: any) => {
        const inactiveStatus = getEntryProjectInactiveStatus(e, projects);
        const isInactive = inactiveStatus !== null;
        return {
          id: e.id,
          status: e.status as TimeEntryStatus,
          isInactive,
          inactiveStatus,
          // The backend decides (it alone sees whether a scrum signed a PENDING_LEAD
          // entry off), unless the project is frozen or closed.
          editable: !isInactive && !!e.ownerEditable,
          projectName: e.project?.name ?? projects.find((p) => p.id === e.projectId)?.name ?? 'Unknown',
          phaseName: e.phase?.name ?? '',
          fromHour: hhmm(e.fromHour),
          toHour: hhmm(e.toHour),
          originalHours: e.hours,
          hours: hours[e.id] ?? e.hours,
          isPending: e.id === pending?.id,
        };
      }),
    [dayEntries, hours, projects, pending, removed],
  );

  const total = rows.reduce((s, r) => s + r.hours, 0) + newRows.reduce((s, r) => s + (r.hours || 0), 0);

  // Removing every entry is a valid way to end a day, so an empty day is
  // saveable as long as the removals are what emptied it.
  const dayIsEmptied = removed.length > 0 && rows.length === 0 && newRows.length === 0;
  const canSave = isDayComplete(total) || dayIsEmptied;

  const setRow = (key: number, patch: Partial<NewRow>) =>
    setNewRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const save = async () => {
    setError(null);

    if ([...rows.filter((r) => r.editable), ...newRows].some((r) => !isHalfHourStep(r.hours))) return setError(HALF_HOUR_ERROR);

    for (const r of newRows) {
      if (!r.projectId) return setError('Select a project for every new entry.');
      const proj = projects.find((p) => p.id === r.projectId);
      if (proj?.status === 'FROZEN') return setError('Cannot log hours for a frozen project.');
      if (proj?.status === 'CLOSED') return setError('Cannot log hours for a closed project.');
      if (!r.phaseId) return setError('Select a phase for every new entry.');
      if (!r.notes.trim()) return setError('Notes are required for every new entry.');
      if (!(r.hours > 0)) return setError('Enter hours for every new entry.');
      if (!routesToDirectManager(r.projectId) && containsInternalMeeting(r.notes)) {
        return setError(INTERNAL_MEETING_ERROR);
      }
    }

    setSaving(true);
    try {
      // Re-stack the day so a longer entry pushes the next one along instead of
      // landing on top of it. Rows the re-layout moves are written too, even
      // when their own hours didn't change.
      const { placed, endsAt } = layOutDay(rows);
      const updates = rows
        .filter(
          (r) =>
            r.editable &&
            (r.isPending || placed[r.id].fromHour !== r.fromHour || placed[r.id].toHour !== r.toHour),
        )
        .map((r) => ({ id: r.id, ...(r.isPending && pending ? pending.body : {}), ...placed[r.id] }));

      let cursor = endsAt;
      const creates = newRows.map((r) => {
        const range = quickHoursToRangeFrom(r.hours, cursor);
        cursor = range.toHour;
        return {
          projectId: r.projectId,
          phaseId: r.phaseId,
          jobTitleId: defaultJobTitleId,
          ...range,
          notes: r.notes,
        };
      });

      // Reachable by undoing a staged deletion until nothing is left to do —
      // the day was already fine, so close rather than ask the API to save
      // an empty change.
      if (updates.length === 0 && creates.length === 0 && removed.length === 0) {
        onSaved();
        return;
      }

      await entriesApi.finalizeDay({ date, updates, creates, deletes: removed });

      queryClient.invalidateQueries({ queryKey: ['my-entries'] });
      queryClient.invalidateQueries({ queryKey: ['my-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['my-entries-for-day'] });
      queryClient.invalidateQueries({ queryKey: ['approvals-queue-count'] });
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to save the day.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Finalize {new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', {
          weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC',
        })}</DialogTitle>
      </DialogHeader>

      <div
        className={`rounded-lg border px-3 py-2 text-sm ${
          canSave
            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-400'
            : 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-400'
        }`}
      >
        <span className="font-medium">Total day entries must be equal {MIN_DAILY_HOURS}</span>
        {'. '}Currently {Math.round(total * 100) / 100}h.
      </div>

      <div className="max-h-[45vh] space-y-2 overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {r.projectName}{r.phaseName && ` · ${r.phaseName}`}
                {r.isPending && <span className="ml-2 text-xs text-muted-foreground">(editing)</span>}
              </p>
              <p className="text-xs text-muted-foreground">
                {r.inactiveStatus === 'FROZEN'
                  ? FROZEN_STATUS_CONFIG.label
                  : r.inactiveStatus === 'CLOSED'
                  ? CLOSED_STATUS_CONFIG.label
                  : STATUS_CONFIG[r.status]?.label ?? r.status}
              </p>
            </div>
            {r.editable ? (
              <>
                <Input
                  type="number" step="0.5" min="0.5" max="12" className="w-24"
                  value={r.hours}
                  onChange={(e) => setHours((h) => ({ ...h, [r.id]: Number(e.target.value) }))}
                />
                <Button
                  variant="ghost" size="icon" aria-label="Remove entry"
                  onClick={() => setRemoved((ids) => [...ids, r.id])}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <Lock className="h-3 w-3" />{r.hours}h
              </span>
            )}
          </div>
        ))}

        {newRows.map((r) => {
          const phases = projects.find((p) => p.id === r.projectId)?.type?.phases ?? [];
          return (
            <div key={r.key} className="space-y-2 rounded-md border border-dashed px-3 py-2">
              <div className="flex gap-2">
                <SearchableSelect
                  value={r.projectId}
                  onValueChange={(v) => setRow(r.key, { projectId: v, phaseId: '' })}
                  options={projects
                    .filter((p) => p.status !== 'FROZEN' && p.status !== 'CLOSED')
                    .map((p) => ({ value: p.id, label: p.name }))}
                  placeholder="Project"
                  searchPlaceholder="Search projects..."
                  className="flex-1"
                />
                <Select value={r.phaseId} onValueChange={(v) => setRow(r.key, { phaseId: v })}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Phase" /></SelectTrigger>
                  <SelectContent>
                    {phases.map((ph) => <SelectItem key={ph.id} value={ph.id}>{ph.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  type="number" step="0.5" min="0.5" max="12" className="w-24" placeholder="Hours"
                  value={r.hours || ''}
                  onChange={(e) => setRow(r.key, { hours: Number(e.target.value) })}
                />
                <Button variant="ghost" size="icon" onClick={() => setNewRows((rs) => rs.filter((x) => x.key !== r.key))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Input placeholder="What did you work on?" value={r.notes} onChange={(e) => setRow(r.key, { notes: e.target.value })} />
            </div>
          );
        })}
      </div>

      <Button
        variant="outline" size="sm" className="self-start"
        onClick={() => setNewRows((rs) => [...rs, { key: Date.now(), projectId: '', phaseId: '', hours: 0, notes: '' }])}
      >
        <PlusCircle className="mr-2 h-4 w-4" />Add entry
      </Button>

      {/* Removals only happen on save, so they stay undoable until then. */}
      {removed.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm text-muted-foreground">
          <span>{removed.length} {removed.length === 1 ? 'entry' : 'entries'} will be deleted when you save.</span>
          <Button variant="ghost" size="sm" onClick={() => setRemoved([])}>Undo</Button>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onBack} disabled={saving}>
          {pending ? 'Back' : 'Cancel'}
        </Button>
        <Button onClick={save} disabled={saving || !canSave}>
          {saving ? 'Saving…' : 'Save day'}
        </Button>
      </div>
    </>
  );
}
